// Top-level dashboard views: DashboardView, ExecutiveDashboard,
// RequestorDashboard.
//
// Each shows a different slice of requests/contracts depending on
// the user role:
//   - DashboardView: legal ops view with portfolio metrics
//   - ExecutiveDashboard: read-only KPIs for executives
//   - RequestorDashboard: my-requests view for business requesters

import { useState, useEffect, useMemo } from "react";
import { T } from "../theme";
import { fmtDate, today } from "../utils";
import { apiCall } from "../apiClient";
import { STATUS_COLORS } from "../constants";
import type { RequestRow, ContractRow, UserRow, LawyerRow } from "../types/api";
import { Btn, Card, Badge, SectionTitle, EmptyState, RefreshBtn } from "./primitives";
import { useClientScope } from "../contexts/ClientScopeContext";

export const DashboardView = ({ requests = [], user = null, repo = [], dashboardStats = null, lawyers = [], onNav = undefined }: { requests?: RequestRow[]; user?: UserRow | null; repo?: ContractRow[]; dashboardStats?: any; lawyers?: LawyerRow[]; onNav?: any }) => {
  const ds = dashboardStats?.totals;
  const [dateRange, setDateRange] = useState("all");
  const [typeFilter, setTypeFilter] = useState("");
  const [myMatters, setMyMatters] = useState<any>(null);
  const { selected: clientScopeSelected } = useClientScope();
  useEffect(() => { apiCall('/dashboard/my-matters').then(r => { if (r) setMyMatters(r); }); }, [clientScopeSelected]);

  const now = new Date();
  const todayStr = today();
  const rangeStart = dateRange === "7d" ? new Date(+now - 7*86400000) : dateRange === "30d" ? new Date(+now - 30*86400000) : dateRange === "90d" ? new Date(+now - 90*86400000) : null;
  const filteredRequests = useMemo(() => {
    let r = requests;
    if (rangeStart) r = r.filter(x => new Date(x.created || 0) >= rangeStart);
    if (typeFilter) r = r.filter(x => x.type === typeFilter);
    return r;
  }, [requests, rangeStart, typeFilter]);

  const stats = useMemo(() => {
    const fr = filteredRequests;
    const active = fr.filter(r => !["Fully Executed", "Cancelled"].includes(r.status || '')).length;
    const overdue = fr.filter(r => r.targetDate && r.targetDate < todayStr && !["Fully Executed", "Cancelled"].includes(r.status || '')).length;
    const pending = fr.filter(r => ["Pending Assignment", "Pending CommOps Assignment", "Pending Legal Assignment"].includes(r.status || '')).length;
    const executed = fr.filter(r => r.status === "Fully Executed").length;
    const totalContracts = ds?.total_contracts ?? repo.length;
    const totalClients = ds?.total_clients ?? 0;
    const expiring = ds?.expiring_90d ?? repo.filter((c: ContractRow) => c.terminationDate! && new Date(c.terminationDate!) > now && new Date(c.terminationDate!) < new Date(now.getTime() + 90 * 86400000)).length;
    const expired = repo.filter((c: ContractRow) => c.terminationDate! && new Date(c.terminationDate!) < now).length;
    const newLast7d = requests.filter((r: RequestRow) => { const d = new Date(r.created || 0); return d >= new Date(+now - 7*86400000); }).length;
    const unassigned = requests.filter((r: RequestRow) => !r.assignedTo && !["Fully Executed", "Cancelled"].includes(r.status || '')).length;
    return { active, overdue, pending, executed, totalContracts, totalClients, expiring, expired, newLast7d, unassigned };
  }, [filteredRequests, repo, ds, todayStr]);

  const statusBreakdown = useMemo(() => {
    const counts: Record<string, any> = {};
    filteredRequests.forEach((r: RequestRow) => { counts[r.status || ''] = (counts[r.status || ''] || 0) + 1; });
    return Object.entries(counts).sort((a: any, b: any) => b[1] - a[1]);
  }, [filteredRequests]);

  const unassignedAging = useMemo(() => {
    const ua = requests.filter((r: RequestRow) => !r.assignedTo && !["Fully Executed", "Cancelled"].includes(r.status || ''));
    const buckets = { "<7d": 0, "7-14d": 0, "14-30d": 0, "30d+": 0 };
    ua.forEach(r => {
      const age = Math.round((+now - +new Date(r.created || 0)) / 86400000);
      if (age < 7) buckets["<7d"]++;
      else if (age < 14) buckets["7-14d"]++;
      else if (age < 30) buckets["14-30d"]++;
      else buckets["30d+"]++;
    });
    return buckets;
  }, [requests]);

  const cycleTime = useMemo(() => {
    const executed = requests.filter((r: RequestRow) => r.status === "Fully Executed" && r.created);
    const assigned = requests.filter((r: RequestRow) => r.assignedTo && r.created);
    const avgToAssign = assigned.length > 0 ? Math.round(assigned.reduce((s: any, r: any) => s + Math.max(0, (+new Date(r.updated || r.created) - +new Date(r.created || 0)) / 86400000), 0) / assigned.length) : 0;
    const avgToExecute = executed.length > 0 ? Math.round(executed.reduce((s: any, r: any) => s + Math.max(0, (+new Date(r.updated || r.created) - +new Date(r.created || 0)) / 86400000), 0) / executed.length) : 0;
    const aging30 = requests.filter((r: RequestRow) => !["Fully Executed", "Cancelled"].includes(r.status || '') && r.created && (+now - +new Date(r.created || 0)) / 86400000 > 30).length;
    return { avgToAssign, avgToExecute, aging30 };
  }, [requests]);

  const dataQuality = useMemo(() => ({
    missingTermination: repo.filter((c: ContractRow) => !c.terminationDate!).length,
    missingEffective: repo.filter((c: ContractRow) => !c.effectiveDate).length,
    missingGoverningLaw: repo.filter((c: ContractRow) => !c.governingLaw).length,
    missingCounterparty: repo.filter((c: ContractRow) => !c.counterparty).length,
    missingClient: repo.filter((c: ContractRow) => !c.clientId).length,
  }), [repo]);

  const renewalContracts = useMemo(() => {
    const withDates = repo.filter((c: ContractRow) => c.terminationDate!).map((c: ContractRow) => ({ ...c, _dl: Math.round((+new Date(c.terminationDate!) - +now) / 86400000) }));
    const expired = withDates.filter((c: ContractRow) => c._dl! < 0).sort((a: any, b: any) => b._dl - a._dl);
    const upcoming = withDates.filter((c: ContractRow) => c._dl! >= 0 && c._dl! <= 365).sort((a: any, b: any) => a._dl - b._dl);
    return [...expired, ...upcoming].slice(0, 8);
  }, [repo]);

  const contractTypes: any[] = useMemo<any[]>(() => {
    if (dashboardStats?.contracts_by_type?.length) return dashboardStats.contracts_by_type;
    const counts: Record<string, any> = {};
    repo.forEach((c: ContractRow) => { const t = c.contractType || "Unknown"; counts[t] = (counts[t] || 0) + 1; });
    return Object.entries(counts).map(([contract_type, count]) => ({ contract_type, count })).sort((a: any, b: any) => b.count - a.count);
  }, [repo, dashboardStats]);

  const requestTypes = useMemo<string[]>(() => {
    const types = new Set(requests.map((r: RequestRow) => r.type).filter(Boolean));
    return Array.from(types).sort() as string[];
  }, [requests]);

  const maxStatus = Math.max(1, ...statusBreakdown.map(([, c]) => c));

  return (
    <div>
      <SectionTitle sub="Contract lifecycle overview" right={
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={{ padding: "6px 10px", fontSize: 11, border: `1px solid ${T.brd}`, borderRadius: 6, backgroundColor: T.bg2, color: T.tx, fontFamily: "inherit" }}>
            <option value="">All Types</option>
            {requestTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          {["7d", "30d", "90d", "all"].map(r => (
            <button key={r} onClick={() => setDateRange(r)} style={{ padding: "5px 10px", fontSize: 10, fontWeight: dateRange === r ? 700 : 400, borderRadius: 14, border: `1px solid ${dateRange === r ? T.acc : T.brd}`, backgroundColor: dateRange === r ? T.accS : "transparent", color: dateRange === r ? T.acc : T.txM, cursor: "pointer", fontFamily: "inherit" }}>{r === "all" ? "All" : r}</button>
          ))}
        </div>
      }>{/* UI label variant, not permission gate. */}{user?.role === "legalops" ? "Legal Operations" : "Dashboard"}</SectionTitle>

      {/* My Matters */}
      {myMatters?.matters?.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 10 }}>My Matters ({myMatters.matters.length})</div>
          <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 6 }}>
            {myMatters.matters.map((m: any) => {
              const sc = STATUS_COLORS[m.status || ''] || { c: "", b: "" };
              const daysSince = Math.round((Date.now() - +new Date(m.updated_at).getTime()) / 86400000);
              return (
                <div key={m.id} onClick={() => onNav && onNav?.('requests', { selectId: m.id })} style={{ minWidth: 200, maxWidth: 240, padding: 12, backgroundColor: T.bg2, border: `1px solid ${T.brd}`, borderRadius: 8, cursor: "pointer", flexShrink: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: T.tx, marginBottom: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.title}</div>
                  {m.counterparty && <div style={{ fontSize: 11, color: T.txD, marginBottom: 6 }}>{m.counterparty}</div>}
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
                    <Badge c={sc.c || T.txM} b={sc.b || T.bg3}>{m.status}</Badge>
                    {m.priority && m.priority !== "Normal" && <Badge c={m.priority === "Urgent" ? T.red : T.orn} b={m.priority === "Urgent" ? T.redS : T.ornS}>{m.priority}</Badge>}
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: 10, color: T.txD }}>
                    <span>{daysSince === 0 ? "Today" : `${daysSince}d ago`}</span>
                    {m.version_count > 0 && <span>{m.version_count} ver.</span>}
                  </div>
                  {m.routing_rule && <span style={{ fontSize: 9, color: T.txD, marginTop: 2, display: "block" }}>{(typeof m.routing_rule === 'string' ? JSON.parse(m.routing_rule || '{}') : m.routing_rule)?.primary_route?.replace(/_/g, ' ')}</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Intake metrics */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10, marginBottom: 24 }}>
        {[
          { label: "New (7 days)", val: stats.newLast7d, c: T.blu, onClick: () => onNav && onNav?.('requests') },
          { label: "Open Requests", val: stats.active, c: T.acc, onClick: () => onNav && onNav?.('requests', { filters: { excludeStatuses: ['Fully Executed', 'Cancelled'] } }) },
          { label: "Unassigned", val: stats.unassigned, c: T.ylw, onClick: () => onNav && onNav?.('requests', { filters: { unassigned: true } }) },
          { label: "Overdue", val: stats.overdue, c: T.red, onClick: () => onNav && onNav?.('requests', { filters: { overdue: true } }) },
          { label: "Total Contracts", val: stats.totalContracts, c: T.prp, onClick: () => onNav && onNav?.('repo') },
          { label: "Active Clients", val: stats.totalClients, c: T.cyn, onClick: () => onNav && onNav?.('repo') },
          { label: "Expiring <90d", val: stats.expiring, c: T.orn, onClick: () => onNav && onNav?.('repo', { filters: { status: 'expiring' } }) },
        ].map(s => (
          <Card key={s.label} style={{ padding: 14, cursor: "pointer" }} onClick={s.onClick}>
            <div style={{ fontSize: 9, fontWeight: 700, color: T.txD, textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 6 }}>{s.label}</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: s.c }}>{s.val}</div>
          </Card>
        ))}
      </div>

      {dashboardStats?.esign && dashboardStats.esign.total_envelopes > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 10 }}>E-Signature Activity</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 10 }}>
            {[
              { label: "Total Envelopes", val: dashboardStats.esign.total_envelopes, c: T.acc, onClick: () => onNav && onNav?.('requests', { filters: { statusIn: ['Sent for Signature', 'Approved for Signature', 'Fully Executed'] } }) },
              { label: "Pending Signature", val: dashboardStats.esign.pending, c: T.ylw, onClick: () => onNav && onNav?.('requests', { filters: { status: 'Sent for Signature' } }) },
              { label: "Completed", val: dashboardStats.esign.completed, c: T.grn, onClick: () => onNav && onNav?.('requests', { filters: { status: 'Fully Executed' } }) },
              { label: "Declined", val: dashboardStats.esign.declined, c: T.red, onClick: () => onNav && onNav?.('requests', { filters: { status: 'Cancelled' } }) },
              { label: "Awaiting Import", val: dashboardStats.esign.awaiting_import, c: T.orn, onClick: () => onNav && onNav?.('requests', { filters: { status: 'Fully Executed' } }) },
              { label: "Avg Hours to Sign", val: dashboardStats.esign.avg_hours_to_sign || "N/A", c: T.prp },
            ].map(s => (
              <Card key={s.label} style={{ padding: 14, textAlign: "center", cursor: s.onClick ? "pointer" : "default" }} onClick={s.onClick}>
                <div style={{ fontSize: 22, fontWeight: 700, color: s.c }}>{s.val}</div>
                <div style={{ fontSize: 10, color: T.txD, marginTop: 4 }}>{s.label}</div>
              </Card>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {/* Requests by status */}
        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 12 }}>Requests by Status</div>
          {statusBreakdown.map(([status, count]) => {
            const sc = STATUS_COLORS[status] || { c: "", b: "" };
            return (<div key={status} onClick={() => onNav && onNav?.('requests', { filters: { status } })} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", cursor: "pointer" }}>
              <span style={{ fontSize: 11, color: T.txM, width: 140, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{status}</span>
              <div style={{ flex: 1, height: 6, borderRadius: 3, backgroundColor: T.bg4, overflow: "hidden" }}><div style={{ width: `${(count / maxStatus) * 100}%`, height: "100%", borderRadius: 3, backgroundColor: sc.c || T.acc }} /></div>
              <span style={{ fontSize: 11, fontWeight: 700, color: T.tx, minWidth: 20, textAlign: "right" }}>{count}</span>
            </div>);
          })}
        </Card>

        {/* Lawyer workload */}
        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 12 }}>Lawyer Workload</div>
          {(lawyers || []).length === 0 && <div style={{ fontSize: 12, color: T.txD, padding: "12px 0" }}>No lawyers configured</div>}
          {(lawyers || []).map((l: any) => {
            const count = requests.filter((r: RequestRow) => r.assignedTo === l.id && !["Fully Executed", "Cancelled"].includes(r.status || '')).length;
            return (<div key={l.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: `1px solid ${T.brdSubtle}`, cursor: "pointer" }} onClick={() => onNav && onNav?.('requests', { filters: { assignedTo: l.id } })}>
              <div style={{ flex: 1 }}><div style={{ fontSize: 12, color: T.tx }}>{l.name}</div><div style={{ fontSize: 10, color: T.txD }}>{l.specialty}</div></div>
              <div style={{ width: 60, height: 5, borderRadius: 3, backgroundColor: T.bg4, overflow: "hidden" }}><div style={{ width: `${Math.min(count * 14, 100)}%`, height: "100%", borderRadius: 3, backgroundColor: count > 5 ? T.red : count > 3 ? T.ylw : T.grn }} /></div>
              <span style={{ fontSize: 11, fontWeight: 700, color: T.txM, minWidth: 14, textAlign: "right" }}>{count}</span>
            </div>);
          })}
        </Card>

        {/* Unassigned backlog aging */}
        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 12 }}>Unassigned Backlog Aging</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
            {Object.entries(unassignedAging).map(([bucket, count]) => (
              <div key={bucket} onClick={() => onNav && onNav?.('requests', { filters: { unassigned: true } })} style={{ padding: 10, backgroundColor: T.bg3, borderRadius: 6, textAlign: "center", cursor: "pointer" }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: count > 0 ? (bucket === "30d+" ? T.red : bucket === "14-30d" ? T.orn : T.ylw) : T.grn }}>{count}</div>
                <div style={{ fontSize: 10, color: T.txD, marginTop: 2 }}>{bucket}</div>
              </div>
            ))}
          </div>
        </Card>

        {/* Cycle time */}
        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 12 }}>Cycle Time</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            <div style={{ padding: 10, backgroundColor: T.bg3, borderRadius: 6, textAlign: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: T.blu }}>{cycleTime.avgToAssign}</div>
              <div style={{ fontSize: 10, color: T.txD, marginTop: 2 }}>Avg Days to Assign</div>
            </div>
            <div style={{ padding: 10, backgroundColor: T.bg3, borderRadius: 6, textAlign: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: T.prp }}>{cycleTime.avgToExecute}</div>
              <div style={{ fontSize: 10, color: T.txD, marginTop: 2 }}>Avg Days to Execute</div>
            </div>
            <div style={{ padding: 10, backgroundColor: T.bg3, borderRadius: 6, textAlign: "center", cursor: "pointer" }} onClick={() => onNav && onNav?.('requests')}>
              <div style={{ fontSize: 20, fontWeight: 800, color: cycleTime.aging30 > 0 ? T.orn : T.grn }}>{cycleTime.aging30}</div>
              <div style={{ fontSize: 10, color: T.txD, marginTop: 2 }}>Aging 30d+</div>
            </div>
          </div>
        </Card>

        {/* By Type */}
        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 12 }}>By Type (Repository)</div>
          {contractTypes.length === 0 && <div style={{ fontSize: 12, color: T.txD, padding: "12px 0" }}>No contracts</div>}
          {contractTypes.slice(0, 10).map(t => (
            <div key={t.contract_type} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${T.brdSubtle}`, cursor: "pointer" }} onClick={() => onNav && onNav?.('repo', { filters: { contractType: t.contract_type } })}>
              <span style={{ fontSize: 12, color: T.txM }}>{t.contract_type}</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: T.tx }}>{t.count}</span>
            </div>
          ))}
        </Card>

        {/* Renewals & expirations */}
        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 12 }}>Renewals & Expirations</div>
          {renewalContracts.length === 0 && <div style={{ fontSize: 12, color: T.txD, padding: "12px 0" }}>No contracts with termination dates</div>}
          {renewalContracts.map(c => (
            <div key={c.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: `1px solid ${T.brdSubtle}`, cursor: "pointer" }} onClick={() => onNav && onNav?.('repo', { filters: { search: c.name } })}>
              <div><div style={{ fontSize: 12, color: T.tx }}>{c.name}</div><div style={{ fontSize: 10, color: T.txD }}>{c.counterparty}</div></div>
              {c._dl! < 0
                ? <Badge c={T.red} b={T.redS}>EXPIRED</Badge>
                : <Badge c={c._dl! < 90 ? T.orn : c._dl! < 180 ? T.ylw : T.txM} b={c._dl! < 90 ? T.ornS : c._dl! < 180 ? T.ylwS : T.bg4}>{c._dl!}d</Badge>
              }
            </div>
          ))}
        </Card>

        {/* Recent requests */}
        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 12 }}>Recent Requests</div>
          {requests.length === 0 && <div style={{ fontSize: 12, color: T.txD, padding: "12px 0" }}>No requests yet</div>}
          {requests.slice(0, 6).map(r => {
            const sc = STATUS_COLORS[r.status || ''] || { c: "", b: "" };
            return (<div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${T.brdSubtle}`, cursor: "pointer" }} onClick={() => onNav && onNav?.('requests', { selectId: r.id })}>
              <div><div style={{ fontSize: 12, color: T.tx, fontWeight: 500 }}>{r.title}</div><div style={{ fontSize: 10, color: T.txD, marginTop: 1 }}>{r.counterparty}</div></div>
              <Badge c={sc.c} b={sc.b} dot>{r.status}</Badge>
            </div>);
          })}
        </Card>

        {/* Data quality */}
        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 12 }}>Data Quality</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {[
              { label: "Missing Termination Date", val: dataQuality.missingTermination, c: T.orn },
              { label: "Missing Effective Date", val: dataQuality.missingEffective, c: T.ylw },
              { label: "Missing Governing Law", val: dataQuality.missingGoverningLaw, c: T.prp },
              { label: "Missing Counterparty", val: dataQuality.missingCounterparty, c: T.red },
              { label: "Missing Client", val: dataQuality.missingClient, c: T.cyn },
            ].map(d => (
              <div key={d.label} style={{ padding: 10, backgroundColor: T.bg3, borderRadius: 6, textAlign: "center" }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: d.val > 0 ? d.c : T.grn }}>{d.val}</div>
                <div style={{ fontSize: 9, color: T.txD, marginTop: 2 }}>{d.label}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};

/* ═══════════════════ EXECUTIVE DASHBOARD ═══════════════════ */
export const ExecutiveDashboard = ({ requests = [], repo = [], dashboardStats = null, lawyers = [], onNav = undefined }: { requests?: RequestRow[]; repo?: ContractRow[]; dashboardStats?: any; lawyers?: LawyerRow[]; onNav?: any }) => {
  const now = new Date();
  const todayStr = today();
  const thisMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const lastMonth = (() => { const d = new Date(now); d.setMonth(d.getMonth() - 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; })();

  const executed = requests.filter((r: RequestRow) => r.status === "Fully Executed");
  const active = requests.filter((r: RequestRow) => !["Fully Executed", "Cancelled"].includes(r.status || ''));
  const executedThisMonth = executed.filter((r: RequestRow) => (r.updated || r.created || "").startsWith(thisMonth));

  const avgTurnaround = useMemo(() => {
    if (executed.length === 0) return 0;
    return Math.round(executed.reduce((s: any, r: any) => s + Math.max(0, (+new Date(r.updated || r.created) - +new Date(r.created || 0)) / 86400000), 0) / executed.length);
  }, [executed]);

  const slaCompliance = useMemo(() => {
    const withTarget = executed.filter((r: RequestRow) => r.targetDate);
    if (withTarget.length === 0) return 100;
    const onTime = withTarget.filter(r => (r.updated || r.created || '') <= (r.targetDate || '')).length;
    return Math.round((onTime / withTarget.length) * 100);
  }, [executed]);

  const byCategory = useMemo(() => {
    const cats: Record<string, number> = { Customer: 0, Vendor: 0, Corporate: 0, Other: 0 };
    repo.forEach((c: ContractRow) => { const cat = c.agreementCategory || "Other"; cats[cat] = (cats[cat] || 0) + 1; });
    return Object.entries(cats).filter(([, v]) => v > 0);
  }, [repo]);

  const topCounterparties = useMemo(() => {
    const counts: Record<string, any> = {};
    repo.forEach((c: ContractRow) => { const cp = c.counterparty || "Unknown"; counts[cp] = (counts[cp] || 0) + 1; });
    return Object.entries(counts).sort((a: any, b: any) => b[1] - a[1]).slice(0, 5);
  }, [repo]);

  const expiring = useMemo(() => ({
    d30: repo.filter((c: ContractRow) => c.terminationDate! && (() => { const dl = (+new Date(c.terminationDate!) - +now) / 86400000; return dl >= 0 && dl <= 30; })()).length,
    d60: repo.filter((c: ContractRow) => c.terminationDate! && (() => { const dl = (+new Date(c.terminationDate!) - +now) / 86400000; return dl > 30 && dl <= 60; })()).length,
    d90: repo.filter((c: ContractRow) => c.terminationDate! && (() => { const dl = (+new Date(c.terminationDate!) - +now) / 86400000; return dl > 60 && dl <= 90; })()).length,
  }), [repo]);

  const requestsPerAttorney = useMemo(() => {
    return (lawyers || []).map((l: any) => ({
      name: l.name,
      active: requests.filter((r: RequestRow) => r.assignedTo === l.id && !["Fully Executed", "Cancelled"].includes(r.status || '')).length,
      total: requests.filter((r: RequestRow) => r.assignedTo === l.id).length,
    })).sort((a: any, b: any) => b.active - a.active);
  }, [requests, lawyers]);

  const thisMonthCount = requests.filter((r: RequestRow) => (r.created || "").startsWith(thisMonth)).length;
  const lastMonthCount = requests.filter((r: RequestRow) => (r.created || "").startsWith(lastMonth)).length;
  const trendPct = lastMonthCount > 0 ? Math.round(((thisMonthCount - lastMonthCount) / lastMonthCount) * 100) : 0;

  const stalled = active.filter((r: RequestRow) => r.updated && (+now - +new Date(r.updated)) / 86400000 > 30);
  const overdue = active.filter((r: RequestRow) => r.targetDate && r.targetDate < todayStr);
  const pendingSig = requests.filter((r: RequestRow) => ["Approved for Signature", "Sent for Signature"].includes(r.status || '') && r.updated && (+now - +new Date(r.updated)) / 86400000 > 14);

  const maxAtty = Math.max(1, ...requestsPerAttorney.map(a => a.active));
  const maxCat = Math.max(1, ...byCategory.map(([, v]) => v));
  const catColors: Record<string, string> = { Customer: T.acc, Vendor: T.prp, Corporate: T.blu, Other: T.txM };

  return (
    <div>
      <SectionTitle sub="Aggregate legal department performance">Executive Overview</SectionTitle>

      {/* KPI row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12, marginBottom: 24 }}>
        {[
          { label: "Total Requests", val: requests.length, c: T.acc },
          { label: "Avg Turnaround", val: `${avgTurnaround}d`, c: T.blu },
          { label: "Open Backlog", val: active.length, c: T.ylw },
          { label: "SLA Compliance", val: `${slaCompliance}%`, c: slaCompliance >= 80 ? T.grn : slaCompliance >= 60 ? T.ylw : T.red },
          { label: "Executed This Month", val: executedThisMonth.length, c: T.grn },
        ].map(k => (
          <Card key={k.label} style={{ padding: 18, textAlign: "center" }}>
            <div style={{ fontSize: 32, fontWeight: 800, color: k.c, lineHeight: 1 }}>{k.val}</div>
            <div style={{ fontSize: 11, color: T.txM, marginTop: 8, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em" }}>{k.label}</div>
          </Card>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {/* Portfolio by category */}
        <Card>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.tx, marginBottom: 14 }}>Portfolio by Category</div>
          {byCategory.map(([cat, count]) => (
            <div key={cat} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0" }}>
              <span style={{ fontSize: 12, color: T.txM, width: 80, flexShrink: 0 }}>{cat}</span>
              <div style={{ flex: 1, height: 8, borderRadius: 4, backgroundColor: T.bg4, overflow: "hidden" }}><div style={{ width: `${(count / maxCat) * 100}%`, height: "100%", borderRadius: 4, backgroundColor: catColors[cat] || T.acc }} /></div>
              <span style={{ fontSize: 12, fontWeight: 700, color: T.tx, minWidth: 28, textAlign: "right" }}>{count}</span>
            </div>
          ))}
        </Card>

        {/* Top counterparties */}
        <Card>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.tx, marginBottom: 14 }}>Top 5 Counterparties</div>
          {topCounterparties.map(([name, count], i) => (
            <div key={name} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${T.brdSubtle}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: T.txD, width: 18 }}>{i + 1}.</span>
                <span style={{ fontSize: 12, color: T.tx }}>{name}</span>
              </div>
              <span style={{ fontSize: 12, fontWeight: 700, color: T.acc }}>{count}</span>
            </div>
          ))}
        </Card>

        {/* Expiring agreements */}
        <Card>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.tx, marginBottom: 14 }}>Expiring Agreements</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            {[
              { label: "30 Days", val: expiring.d30, c: T.red },
              { label: "60 Days", val: expiring.d60, c: T.orn },
              { label: "90 Days", val: expiring.d90, c: T.ylw },
            ].map(e => (
              <div key={e.label} style={{ padding: 14, backgroundColor: T.bg3, borderRadius: 8, textAlign: "center" }}>
                <div style={{ fontSize: 24, fontWeight: 800, color: e.val > 0 ? e.c : T.grn }}>{e.val}</div>
                <div style={{ fontSize: 10, color: T.txD, marginTop: 4 }}>{e.label}</div>
              </div>
            ))}
          </div>
        </Card>

        {/* Workload trend */}
        <Card>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.tx, marginBottom: 14 }}>Request Volume Trend</div>
          <div style={{ display: "flex", alignItems: "center", gap: 20, padding: "10px 0" }}>
            <div style={{ textAlign: "center", flex: 1 }}>
              <div style={{ fontSize: 10, color: T.txD, marginBottom: 4 }}>Last Month</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: T.txM }}>{lastMonthCount}</div>
            </div>
            <div style={{ fontSize: 20, color: trendPct > 0 ? T.red : trendPct < 0 ? T.grn : T.txM }}>{trendPct > 0 ? `+${trendPct}%` : trendPct < 0 ? `${trendPct}%` : "—"}</div>
            <div style={{ textAlign: "center", flex: 1 }}>
              <div style={{ fontSize: 10, color: T.txD, marginBottom: 4 }}>This Month</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: T.acc }}>{thisMonthCount}</div>
            </div>
          </div>
        </Card>

        {/* Requests per attorney */}
        <Card>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.tx, marginBottom: 14 }}>Requests per Attorney</div>
          {requestsPerAttorney.map(a => (
            <div key={a.name} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0" }}>
              <span style={{ fontSize: 12, color: T.txM, width: 120, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</span>
              <div style={{ flex: 1, height: 8, borderRadius: 4, backgroundColor: T.bg4, overflow: "hidden" }}><div style={{ width: `${(a.active / maxAtty) * 100}%`, height: "100%", borderRadius: 4, backgroundColor: a.active > 6 ? T.red : a.active > 3 ? T.ylw : T.grn }} /></div>
              <span style={{ fontSize: 11, fontWeight: 700, color: T.tx, minWidth: 20, textAlign: "right" }}>{a.active}</span>
            </div>
          ))}
          {requestsPerAttorney.length === 0 && <div style={{ fontSize: 12, color: T.txD, padding: "12px 0" }}>No data</div>}
        </Card>

        {/* Risk indicators */}
        <Card>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.tx, marginBottom: 14 }}>Risk Indicators</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            {[
              { label: "Stalled >30d", val: stalled.length, c: T.red },
              { label: "Overdue", val: overdue.length, c: T.orn },
              { label: "Pending Signature >14d", val: pendingSig.length, c: T.ylw },
            ].map(r => (
              <div key={r.label} style={{ padding: 14, backgroundColor: r.val > 0 ? T.redS : T.bg3, borderRadius: 8, textAlign: "center" }}>
                <div style={{ fontSize: 24, fontWeight: 800, color: r.val > 0 ? r.c : T.grn }}>{r.val}</div>
                <div style={{ fontSize: 10, color: T.txD, marginTop: 4 }}>{r.label}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};


/* ═══════════════════ REQUESTOR DASHBOARD ═══════════════════ */
export const RequestorDashboard = ({ requests = [], user = null, onSelect = undefined }: { requests?: RequestRow[]; user?: UserRow | null; onSelect?: (req: RequestRow) => void }) => {
  const myRequests = requests.filter((r: RequestRow) => r.requester === user?.name || r.requesterEmail === user?.email);

  const submitted = myRequests.filter((r: RequestRow) => ["Pending Assignment", "Pending CommOps Assignment", "Pending Legal Assignment"].includes(r.status || ''));
  const inReview = myRequests.filter((r: RequestRow) => ["Legal Review", "Business Review", "Counterparty Review"].includes(r.status || ''));
  const waitingOnMe = myRequests.filter((r: RequestRow) => r.status === "Needs More Information");
  const approved = myRequests.filter((r: RequestRow) => ["Legal Review Completed", "Approved for Signature"].includes(r.status || ''));
  const executed = myRequests.filter((r: RequestRow) => r.status === "Fully Executed");

  const [tab, setTab] = useState("all");

  const tabs = [
    { id: "all", label: "All Requests", count: myRequests.length },
    { id: "action", label: "Action Required", count: waitingOnMe.length },
    { id: "inReview", label: "In Review", count: inReview.length },
    { id: "submitted", label: "Submitted", count: submitted.length },
    { id: "completed", label: "Completed", count: executed.length },
  ];

  const filtered = (() => {
    switch (tab) {
      case "action": return waitingOnMe;
      case "inReview": return inReview;
      case "submitted": return submitted;
      case "completed": return executed;
      default: return myRequests;
    }
  })();

  return (
    <div>
      <SectionTitle sub={`${myRequests.length} total requests`}>My Requests</SectionTitle>

      {/* Summary cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12, marginBottom: 20 }}>
        <div style={{ padding: 14, backgroundColor: T.bg3, borderRadius: 8, borderLeft: `3px solid ${T.orn}`, textAlign: "center" }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: T.orn }}>{submitted.length}</div>
          <div style={{ fontSize: 10, color: T.txM }}>Submitted</div>
        </div>
        <div style={{ padding: 14, backgroundColor: T.bg3, borderRadius: 8, borderLeft: `3px solid ${T.blu}`, textAlign: "center" }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: T.blu }}>{inReview.length}</div>
          <div style={{ fontSize: 10, color: T.txM }}>In Review</div>
        </div>
        <div style={{ padding: 14, backgroundColor: waitingOnMe.length > 0 ? T.redS : T.bg3, borderRadius: 8, borderLeft: `3px solid ${T.red}`, textAlign: "center" }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: T.red }}>{waitingOnMe.length}</div>
          <div style={{ fontSize: 10, color: T.txM }}>Waiting on Me</div>
        </div>
        <div style={{ padding: 14, backgroundColor: T.bg3, borderRadius: 8, borderLeft: `3px solid ${T.grn}`, textAlign: "center" }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: T.grn }}>{approved.length}</div>
          <div style={{ fontSize: 10, color: T.txM }}>Approved</div>
        </div>
        <div style={{ padding: 14, backgroundColor: T.bg3, borderRadius: 8, borderLeft: `3px solid ${T.grn}`, textAlign: "center" }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: T.grn }}>{executed.length}</div>
          <div style={{ fontSize: 10, color: T.txM }}>Executed</div>
        </div>
      </div>

      {/* Tab filters */}
      <div style={{ display: "flex", gap: 4, marginBottom: 16 }}>
        {tabs.map((t: any) => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ padding: "6px 14px", fontSize: 11, fontWeight: tab === t.id ? 700 : 400, borderRadius: 16, border: `1px solid ${tab === t.id ? T.acc : T.brd}`, backgroundColor: tab === t.id ? T.accS : "transparent", color: tab === t.id ? T.acc : T.txM, cursor: "pointer", fontFamily: "inherit" }}>
            {t.label} ({t.count})
          </button>
        ))}
      </div>

      {/* Request list */}
      <Card>
        {filtered.length === 0 && <div style={{ fontSize: 12, color: T.txM, padding: "16px 0", textAlign: "center" }}>No requests in this category</div>}
        {filtered.map(r => (
          <div key={r.id} onClick={() => onSelect && onSelect?.(r)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: `1px solid ${T.brdSubtle}`, cursor: "pointer" }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.tx }}>{r.title}</div>
              <div style={{ fontSize: 11, color: T.txM }}>{r.counterparty} · {r.type} · {fmtDate(r.created)}</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Badge c={STATUS_COLORS[r.status || '']?.c || T.txM} b={STATUS_COLORS[r.status || '']?.b || T.bg3}>{r.status}</Badge>
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
};